import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Agent } from '../types/Agents';
import type { Contact } from '../types/Contact';
import { RootState } from './store';
import { agentsApi, type CreateAgentInput } from '../services/api';
import i18next from 'i18next';

type AgentSettingsUpdate = {
  name?: string;
  context?: string;
  description?: string;
  notification_phone_number?: string | null;
  business_info?: Agent['business_info'];
  config?: Agent['config'];
};

interface AgentsState {
  items: Agent[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: AgentsState = {
  items: [],
  status: 'idle',
  error: null,
};

// Async Thunks
export const fetchAgents = createAsyncThunk('agents/fetchAgents', async () => {
  try {
    return await agentsApi.fetchAgents();
  } catch (error) {
    throw new Error(
      i18next.t('redux.agents.errors.fetch_failed.translation', { error })
    );
  }
});

export const createAgent = createAsyncThunk(
  'agents/createAgent',
  async (agent: CreateAgentInput) => {
    try {
      return await agentsApi.createAgent(agent);
    } catch (error) {
      throw new Error(
        i18next.t('redux.agents.errors.create_failed.translation', { error })
      );
    }
  }
);

export const updateAgentSettings = createAsyncThunk(
  'agents/updateAgentSettings',
  async (params: { agentId: number; settings: AgentSettingsUpdate }) => {
    try {
      return await agentsApi.updateAgentSettings(params);
    } catch (error) {
      throw new Error(
        i18next.t('redux.agents.errors.update_failed.translation', { error })
      );
    }
  }
);

export const deleteAgent = createAsyncThunk(
  'agents/deleteAgent',
  async (agentId: number) => {
    try {
      return await agentsApi.handleDelete(agentId);
    } catch (error) {
      throw new Error(
        i18next.t('redux.agents.errors.delete_failed.translation', { error })
      );
    }
  }
);

export const updateContact = createAsyncThunk(
  'agents/updateContact',
  async ({
    contactId,
    updates,
  }: {
    contactId: number;
    updates: { name: string };
  }) => {
    try {
      const { contactsApi } = await import('../services/api');
      return await contactsApi.updateContact(contactId, updates);
    } catch (error) {
      throw new Error(
        i18next.t('redux.contacts.errors.update_failed.translation', { error })
      );
    }
  }
);

// Slice
const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    resetAgentsState: () => initialState,
    setAgentSettings: (
      state,
      action: PayloadAction<{ agentId: number; settings: AgentSettingsUpdate }>
    ) => {
      const { agentId, settings } = action.payload;
      const agent = state.items.find((a: Agent) => a.id === agentId);
      if (agent) {
        Object.assign(agent, settings);
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Agents
      .addCase(fetchAgents.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload ?? [];
        state.error = null;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch agents';
      })
      // Create Agent
      .addCase(createAgent.fulfilled, (state, action) => {
        state.items.push(action.payload.agent);
        state.error = null;
      })
      // Update Agent Settings
      .addCase(updateAgentSettings.fulfilled, (state, action) => {
        const updatedAgent = action.payload;
        const index = state.items.findIndex(
          (a: Agent) => a.id === updatedAgent.id
        );
        if (index !== -1) {
          state.items[index] = {
            ...state.items[index],
            ...updatedAgent,
          };
        }
        state.error = null;
      })
      // Delete Agent
      .addCase(deleteAgent.fulfilled, (state, action) => {
        const agentId = action.meta.arg; // The agentId passed to the thunk
        state.items = state.items.filter((a: Agent) => a.id !== agentId);
        state.error = null;
      })
      // Update Contact
      .addCase(updateContact.fulfilled, (state, action) => {
        const updatedContact = action.payload;

        // Find the agent that contains this contact and update it
        state.items = state.items.map(agent => {
          if (agent.contacts) {
            const contactIndex = agent.contacts.findIndex(
              (c: Contact) => c.id === updatedContact.id
            );
            if (contactIndex !== -1) {
              // Create a new contacts array with the updated contact
              const updatedContacts = [...agent.contacts];
              updatedContacts[contactIndex] = updatedContact;

              // Return a new agent object with the updated contacts
              const updatedAgent = {
                ...agent,
                contacts: updatedContacts,
              };

              return updatedAgent;
            }
          }
          return agent;
        });
      });
  },
});

// Actions
export const { resetAgentsState, setAgentSettings } = agentsSlice.actions;

// Selectors
export const selectAgents = (state: RootState) => state.agents.items;
export const selectAgentsStatus = (state: RootState) => state.agents.status;
export const selectAgentsError = (state: RootState) => state.agents.error;
export const selectAgentById = (state: RootState, agentId: number) =>
  state.agents.items.find((a: Agent) => a.id === agentId);

export default agentsSlice.reducer;
